import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Modal, Pagination, Select, SelectItem, TextArea, TextInput } from '@carbon/react';
import { Add } from '@carbon/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { DataTable } from '../components/DataTable';
import { EmptyState, ErrorState, InlineError, PageHeader } from '../components/states';
import { CURRENCY, When } from '../components/format';

// Player segments (P1-D24): a saved rule counted live over the CMS copy of the game data. Used as the
// audience of inbox messages, giftcodes and campaigns.

export type Rule = {
  registered?: { withinDays?: number; olderThanDays?: number };
  deposit?: { has?: 'yes' | 'no'; days?: number; minTotal?: number; maxTotal?: number; minCount?: number };
  activity?: { seenWithinDays?: number; notSeenForDays?: number; minTurnover?: number; turnoverDays?: number };
  vip?: { min?: number; max?: number };
  loss?: { days: number; minNet: number };
  agent?: { codes: string[]; tree?: boolean };
  source?: { sources?: string[]; mediums?: string[]; campaigns?: string[] };
};
export type Segment = { id: string; name: string; note: string; rule: Rule; count: number | null; createdAt: string; updatedAt: string };
type Member = { id: number; username: string; nickname: string | null; createdAt: string; ftdAt: string | null; lastSeenAt: string | null; vipPoint: string; regSource: string | null };

// the form keeps every field as text; empty = not part of the rule
const FIELDS = [
  'regWithin', 'regOlder', 'depHas', 'depDays', 'depMin', 'depMax', 'depCount', 'seenWithin', 'notSeen', 'turnover', 'turnoverDays',
  'vipMin', 'vipMax', 'lossDays', 'lossMin', 'agents', 'sources', 'mediums', 'campaigns',
] as const;
type Form = Record<(typeof FIELDS)[number], string> & { agentTree: boolean; noSource: boolean };
const blank: Form = { ...(Object.fromEntries(FIELDS.map((f) => [f, ''])) as Record<(typeof FIELDS)[number], string>), agentTree: false, noSource: false };

const num = (s: string) => (s === '' ? undefined : Number(s));
const list = (s: string) => s.split(/[,;\n]+/).map((x) => x.trim()).filter(Boolean);
const clean = <T extends object>(o: T): T | undefined => {
  const e = Object.entries(o).filter(([, v]) => v !== undefined && v !== false);
  return e.length ? (Object.fromEntries(e) as T) : undefined;
};

export function ruleOf(f: Form): Rule {
  const sources = [...list(f.sources), ...(f.noSource ? [''] : [])];
  const r: Rule = {
    registered: clean({ withinDays: num(f.regWithin), olderThanDays: num(f.regOlder) }),
    deposit: clean({ has: (f.depHas || undefined) as 'yes' | 'no' | undefined, days: num(f.depDays), minTotal: num(f.depMin), maxTotal: num(f.depMax), minCount: num(f.depCount) }),
    activity: clean({ seenWithinDays: num(f.seenWithin), notSeenForDays: num(f.notSeen), minTurnover: num(f.turnover), turnoverDays: num(f.turnoverDays) }),
    vip: clean({ min: num(f.vipMin), max: num(f.vipMax) }),
    loss: f.lossMin !== '' ? { days: Number(f.lossDays || 7), minNet: Number(f.lossMin) } : undefined,
    agent: list(f.agents).length ? { codes: list(f.agents), ...(f.agentTree ? { tree: true } : {}) } : undefined,
    source: clean({ sources: sources.length ? sources : undefined, mediums: list(f.mediums).length ? list(f.mediums) : undefined, campaigns: list(f.campaigns).length ? list(f.campaigns) : undefined }),
  };
  // a deposit window alone filters nothing
  if (r.deposit && Object.keys(r.deposit).every((k) => k === 'days')) delete r.deposit;
  return clean(r) ?? {};
}

function formOf(r: Rule): Form {
  const s = (v: number | undefined) => (v == null ? '' : String(v));
  const src = r.source?.sources ?? [];
  return {
    regWithin: s(r.registered?.withinDays), regOlder: s(r.registered?.olderThanDays),
    depHas: r.deposit?.has ?? '', depDays: s(r.deposit?.days), depMin: s(r.deposit?.minTotal), depMax: s(r.deposit?.maxTotal), depCount: s(r.deposit?.minCount),
    seenWithin: s(r.activity?.seenWithinDays), notSeen: s(r.activity?.notSeenForDays), turnover: s(r.activity?.minTurnover), turnoverDays: s(r.activity?.turnoverDays),
    vipMin: s(r.vip?.min), vipMax: s(r.vip?.max), lossDays: s(r.loss?.days), lossMin: s(r.loss?.minNet),
    agents: (r.agent?.codes ?? []).join(', '), agentTree: !!r.agent?.tree,
    sources: src.filter(Boolean).join(', '), noSource: src.includes(''), mediums: (r.source?.mediums ?? []).join(', '), campaigns: (r.source?.campaigns ?? []).join(', '),
  };
}

// one line per rule part, for the table and the audience pickers
export function ruleText(r: Rule): string {
  const out: string[] = [];
  const m = (v: number) => v.toLocaleString('vi-VN');
  if (r.registered?.withinDays) out.push(`đăng ký ≤ ${r.registered.withinDays} ngày`);
  if (r.registered?.olderThanDays) out.push(`đăng ký > ${r.registered.olderThanDays} ngày`);
  const d = r.deposit;
  if (d) {
    const w = d.days ? ` trong ${d.days} ngày` : '';
    if (d.has === 'yes') out.push('đã nạp' + w);
    if (d.has === 'no') out.push('chưa nạp' + w);
    if (d.minTotal != null) out.push(`nạp ≥ ${m(d.minTotal)}${w}`);
    if (d.maxTotal != null) out.push(`nạp ≤ ${m(d.maxTotal)}${w}`);
    if (d.minCount != null) out.push(`≥ ${d.minCount} lần nạp${w}`);
  }
  const a = r.activity;
  if (a?.seenWithinDays) out.push(`vào game trong ${a.seenWithinDays} ngày`);
  if (a?.notSeenForDays) out.push(`vắng ≥ ${a.notSeenForDays} ngày`);
  if (a?.minTurnover != null) out.push(`cược ≥ ${m(a.minTurnover)} trong ${a.turnoverDays ?? 30} ngày`);
  if (r.vip?.min != null) out.push(`VIP ≥ ${m(r.vip.min)}`);
  if (r.vip?.max != null) out.push(`VIP ≤ ${m(r.vip.max)}`);
  if (r.loss) out.push(`thua ròng ≥ ${m(r.loss.minNet)} trong ${r.loss.days} ngày`);
  if (r.agent) out.push(`đại lý ${r.agent.codes.join(', ')}${r.agent.tree ? ' (cả tuyến dưới)' : ''}`);
  if (r.source?.sources) out.push(`nguồn ${r.source.sources.map((x) => x || '(không có)').join(', ')}`);
  if (r.source?.mediums) out.push(`kênh ${r.source.mediums.join(', ')}`);
  if (r.source?.campaigns) out.push(`chiến dịch ${r.source.campaigns.join(', ')}`);
  return out.length ? out.join(' · ') : 'Mọi người chơi';
}

export function useSegments(enabled = true) {
  return useQuery({ queryKey: ['segments'], queryFn: () => api.get<{ items: Segment[] }>('/api/admin/segments'), enabled });
}

// A picker for the audience forms: '' = no segment
export function SegmentSelect({ id, value, onChange, helperText }: { id: string; value: string; onChange: (v: string) => void; helperText?: string }) {
  const q = useSegments();
  return (
    <Select id={id} labelText="Phân khúc người chơi" value={value} onChange={(e) => onChange(e.target.value)} disabled={q.isLoading}
      helperText={q.error ? 'Không tải được danh sách phân khúc' : helperText}>
      <SelectItem value="" text="Không dùng phân khúc" />
      {(q.data?.items ?? []).map((s) => <SelectItem key={s.id} value={s.id} text={`${s.name} (${s.count == null ? '?' : s.count.toLocaleString('vi-VN')} người)`} />)}
    </Select>
  );
}

const memberCols: ColumnDef<Member, any>[] = [
  { header: 'Mã', accessorKey: 'id', cell: (c) => <a className="cell-link" href={'/users/' + c.getValue()}>{c.getValue()}</a> },
  { header: 'Tên', id: 'n', cell: (c) => c.row.original.nickname || c.row.original.username },
  { header: 'Đăng ký', accessorKey: 'createdAt', cell: (c) => <When v={c.getValue()} /> },
  { header: 'Nạp đầu', accessorKey: 'ftdAt', cell: (c) => c.getValue() ? <When v={c.getValue()} /> : <span className="muted">Chưa nạp</span> },
  { header: 'Vào game gần nhất', accessorKey: 'lastSeenAt', cell: (c) => c.getValue() ? <When v={c.getValue()} /> : <span className="muted">-</span> },
  { header: 'Điểm VIP', accessorKey: 'vipPoint', cell: (c) => <span className="num">{Number(c.getValue()).toLocaleString('vi-VN')}</span> },
  { header: 'Nguồn', accessorKey: 'regSource', cell: (c) => c.getValue() || <span className="muted">-</span> },
];

function RuleFields({ f, set }: { f: Form; set: (f: Form) => void }) {
  const d = (k: keyof Form) => (e: { target: { value: string } }) => set({ ...f, [k]: e.target.value.replace(/\D/g, '').slice(0, 15) });
  const t = (k: keyof Form) => (e: { target: { value: string } }) => set({ ...f, [k]: e.target.value });
  const money = `(${CURRENCY.symbol})`;
  return (
    <>
      <h4>Đăng ký</h4>
      <div className="grid2">
        <TextInput id="sg-rw" labelText="Đăng ký trong vòng (ngày)" inputMode="numeric" value={f.regWithin} onChange={d('regWithin')} />
        <TextInput id="sg-ro" labelText="Đăng ký đã lâu hơn (ngày)" inputMode="numeric" value={f.regOlder} onChange={d('regOlder')} />
      </div>
      <h4>Nạp tiền</h4>
      <div className="grid2">
        <Select id="sg-dh" labelText="Đã nạp" value={f.depHas} onChange={t('depHas')}>
          <SelectItem value="" text="Không lọc" />
          <SelectItem value="yes" text="Đã nạp" />
          <SelectItem value="no" text="Chưa nạp" />
        </Select>
        <TextInput id="sg-dd" labelText="Trong (ngày gần nhất)" helperText="Để trống = từ trước tới nay" inputMode="numeric" value={f.depDays} onChange={d('depDays')} />
        <TextInput id="sg-dmin" labelText={`Tổng nạp từ ${money}`} inputMode="numeric" value={f.depMin} onChange={d('depMin')} />
        <TextInput id="sg-dmax" labelText={`Tổng nạp đến ${money}`} inputMode="numeric" value={f.depMax} onChange={d('depMax')} />
        <TextInput id="sg-dc" labelText="Số lần nạp tối thiểu" inputMode="numeric" value={f.depCount} onChange={d('depCount')} />
      </div>
      <h4>Hoạt động</h4>
      <div className="grid2">
        <TextInput id="sg-sw" labelText="Vào game trong (ngày gần nhất)" inputMode="numeric" value={f.seenWithin} onChange={d('seenWithin')} />
        <TextInput id="sg-ns" labelText="Vắng mặt ít nhất (ngày)" inputMode="numeric" value={f.notSeen} onChange={d('notSeen')} />
        <TextInput id="sg-tv" labelText={`Tổng cược tối thiểu ${money}`} helperText="Cược trừ tiền hoàn" inputMode="numeric" value={f.turnover} onChange={d('turnover')} />
        <TextInput id="sg-tvd" labelText="Tổng cược tính trong (ngày)" helperText="Để trống = 30 ngày" inputMode="numeric" value={f.turnoverDays} onChange={d('turnoverDays')} />
      </div>
      <h4>VIP và thua lỗ</h4>
      <div className="grid2">
        <TextInput id="sg-vmin" labelText="Điểm VIP từ" inputMode="numeric" value={f.vipMin} onChange={d('vipMin')} />
        <TextInput id="sg-vmax" labelText="Điểm VIP đến" inputMode="numeric" value={f.vipMax} onChange={d('vipMax')} />
        <TextInput id="sg-lm" labelText={`Thua ròng tối thiểu ${money}`} helperText="Cược trừ thắng và tiền hoàn" inputMode="numeric" value={f.lossMin} onChange={d('lossMin')} />
        <TextInput id="sg-ld" labelText="Thua ròng tính trong (ngày)" helperText="Để trống = 7 ngày" inputMode="numeric" value={f.lossDays} onChange={d('lossDays')} />
      </div>
      <h4>Đại lý và nguồn</h4>
      <TextInput id="sg-ag" labelText="Mã đại lý (cách nhau dấu phẩy)" helperText="Mã nhà cái = người chơi không qua đại lý" value={f.agents} onChange={t('agents')} />
      <Checkbox id="sg-at" labelText="Gồm cả tuyến dưới của các đại lý này" checked={f.agentTree} onChange={(_, { checked }) => set({ ...f, agentTree: checked })} />
      <TextInput id="sg-src" labelText="Nguồn đăng ký (utm_source, cách nhau dấu phẩy)" value={f.sources} onChange={t('sources')} />
      <Checkbox id="sg-ns0" labelText="Gồm người chơi đăng ký không có nguồn" checked={f.noSource} onChange={(_, { checked }) => set({ ...f, noSource: checked })} />
      <div className="grid2">
        <TextInput id="sg-med" labelText="Kênh (utm_medium)" value={f.mediums} onChange={t('mediums')} />
        <TextInput id="sg-cmp" labelText="Chiến dịch (utm_campaign)" value={f.campaigns} onChange={t('campaigns')} />
      </div>
    </>
  );
}

function SegmentDialog({ seg, onClose }: { seg: Segment | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(seg?.name ?? '');
  const [note, setNote] = useState(seg?.note ?? '');
  const [f, setF] = useState<Form>(seg ? formOf(seg.rule) : blank);
  const rule = ruleOf(f);
  const key = JSON.stringify(rule);
  // live count: preview the rule half a second after the last change
  const [debounced, setDebounced] = useState(key);
  useEffect(() => { const h = setTimeout(() => setDebounced(key), 500); return () => clearTimeout(h); }, [key]);
  const preview = useQuery({
    queryKey: ['segment-preview', debounced],
    queryFn: () => api.post<{ count: number; sample: Member[] }>('/api/admin/segments/preview', { rule: JSON.parse(debounced) }),
    placeholderData: (p) => p,
  });
  const done = () => { qc.invalidateQueries({ queryKey: ['segments'] }); onClose(); };
  const save = useMutation({
    mutationFn: () => seg ? api.patch('/api/admin/segments/' + seg.id, { name, note, rule }) : api.post('/api/admin/segments', { name, note, rule }),
    onSuccess: done,
  });
  const [confirmDel, setConfirmDel] = useState(false);
  const del = useMutation({ mutationFn: () => api.del('/api/admin/segments/' + seg!.id), onSuccess: done });
  return (
    <>
      <Modal open={!confirmDel} size="lg" modalHeading={seg ? 'Sửa phân khúc' : 'Tạo phân khúc'} primaryButtonText="Lưu" secondaryButtonText="Hủy"
        primaryButtonDisabled={name.trim().length < 2 || save.isPending} onRequestClose={onClose} onRequestSubmit={() => save.mutate()}>
        <div className="stack">
          <TextInput id="sg-name" labelText="Tên phân khúc" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
          <TextArea id="sg-note" labelText="Ghi chú (tùy chọn)" rows={2} maxCount={500} enableCounter value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="kpis">
            <div className="kpi"><span>Số người chơi lúc này</span>
              <strong className="num">{preview.isLoading ? '...' : preview.data ? preview.data.count.toLocaleString('vi-VN') : '-'}</strong></div>
          </div>
          <p>{ruleText(rule)}</p>
          <p className="muted">Người chơi phải thỏa mọi điều kiện đã điền. Bot, tài khoản thử, tài khoản bị khóa và người đang tự khóa luôn bị loại.</p>
          <RuleFields f={f} set={setF} />
          {preview.error ? <InlineError error={preview.error} /> : preview.data?.sample.length ? (
            <DataTable columns={memberCols} data={preview.data.sample} rowKey={(m) => String(m.id)} empty={{ title: 'Không có ai' }} title="20 người chơi đầu" />
          ) : preview.data ? <EmptyState title="Chưa có người chơi nào thỏa điều kiện" /> : null}
          {seg ? <div className="row"><Button size="sm" kind="danger--ghost" onClick={() => { del.reset(); setConfirmDel(true); }}>Xóa phân khúc</Button></div> : null}
          <InlineError error={save.error} />
        </div>
      </Modal>
      <Modal open={confirmDel} danger modalHeading={'Xóa ' + (seg?.name ?? '')} primaryButtonText="Xóa" secondaryButtonText="Hủy"
        primaryButtonDisabled={del.isPending} onRequestClose={() => setConfirmDel(false)} onRequestSubmit={() => del.mutate()}>
        <div className="stack">
          <p>Không xóa được khi phân khúc còn là đối tượng của một giftcode hoặc sự kiện đang chạy. Thư đã gửi không bị ảnh hưởng.</p>
          <InlineError error={del.error} />
        </div>
      </Modal>
    </>
  );
}

function MembersDialog({ seg, onClose }: { seg: Segment; onClose: () => void }) {
  const [page, setPage] = useState({ page: 1, pageSize: 50 });
  const q = useQuery({
    queryKey: ['segment-members', seg.id, page],
    queryFn: () => api.get<{ items: Member[]; total: number }>(`/api/admin/segments/${seg.id}/members`, page),
    placeholderData: (p) => p,
  });
  return (
    <Modal open size="lg" passiveModal modalHeading={`${seg.name}: người chơi`} onRequestClose={onClose}>
      {q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : (
        <DataTable columns={memberCols} data={q.data?.items} loading={q.isLoading} rowKey={(m) => String(m.id)}
          empty={{ title: 'Phân khúc đang trống' }}
          footer={q.data ? <Pagination page={page.page} pageSize={page.pageSize} pageSizes={[50, 100, 200]} totalItems={q.data.total}
            itemsPerPageText="Mỗi trang" onChange={({ page: p, pageSize }) => setPage({ page: p, pageSize })} /> : null} />
      )}
    </Modal>
  );
}

export function SegmentsPage() {
  const q = useSegments();
  const [edit, setEdit] = useState<{ seg: Segment | null } | null>(null);
  const [members, setMembers] = useState<Segment | null>(null);
  const columns: ColumnDef<Segment, any>[] = [
    { header: 'Tên', accessorKey: 'name' },
    { header: 'Điều kiện', id: 'rule', enableSorting: false, cell: (c) => <span className="muted">{ruleText(c.row.original.rule)}</span> },
    { header: 'Số người lúc này', accessorKey: 'count', cell: (c) => c.getValue() == null ? <span className="muted">Lỗi điều kiện</span> : <span className="num">{Number(c.getValue()).toLocaleString('vi-VN')}</span> },
    { header: 'Sửa lúc', accessorKey: 'updatedAt', cell: (c) => <When v={c.getValue()} /> },
    { header: '', id: 'act', enableSorting: false, cell: (c) => <Button size="sm" kind="ghost" onClick={(e) => { e.stopPropagation(); setMembers(c.row.original); }}>Xem người chơi</Button> },
  ];
  return (
    <div className="page">
      <PageHeader title="Phân khúc CRM"
        lead="Nhóm người chơi theo nạp tiền, hoạt động, VIP, thua lỗ, đại lý và nguồn. Số người đếm lại mỗi lần mở. Dùng làm đối tượng cho hộp thư, giftcode và sự kiện."
        actions={<Button size="sm" renderIcon={Add} onClick={() => setEdit({ seg: null })}>Tạo phân khúc</Button>} />
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading} error={q.error} onRetry={() => q.refetch()}
        onRowClick={(s) => setEdit({ seg: s })} rowKey={(s) => s.id}
        empty={{ title: 'Chưa có phân khúc', body: 'Tạo phân khúc để gửi thư, giftcode hoặc sự kiện cho đúng nhóm người chơi.' }} />
      {edit ? <SegmentDialog key={edit.seg?.id ?? 'new'} seg={edit.seg} onClose={() => setEdit(null)} /> : null}
      {members ? <MembersDialog seg={members} onClose={() => setMembers(null)} /> : null}
    </div>
  );
}
